import React from 'react';
import { connect } from 'react-redux';
import { errorSelectors } from 'redux-query';
import { RouteComponentProps, Redirect } from '@reach/router';
import { Button2 } from '@el8/vital';
import Content from 'views/booking/common/Content/Content';
import { setPatientInfo } from 'modules/account';
import { updateBookingPatientInfo, getBookingPatientInfo, getBookingInfo } from 'modules/booking';
import { logout } from 'modules/patient';
import LoginCard from 'views/account/LoginCard';
import AppointmentSummary from 'views/booking/AppointmentSummary/AppointmentSummary';
import { logEvent } from 'utils/amplitude';

import {
  State,
  UpdateBookingPatientInfo,
  FailureResponse,
  Dispatch,
  BookingState,
  PatientChartInfo,
  SessionState,
} from 'types';

type OwnProps = {
  practiceId?: number;
} & RouteComponentProps;

type StateProps = {
  patientInfo: PatientChartInfo;
  loginError?: FailureResponse;
  bookingInfo: Partial<BookingState>;
  session: SessionState;
};

type DispatchProps = {
  clearPatientData: () => UpdateBookingPatientInfo;
  createAccount: (patient: PatientChartInfo) => void;
  updateEmail: (email: string) => UpdateBookingPatientInfo;
};

type Props = OwnProps & StateProps & DispatchProps;

const mapStateToProps = (state: State): StateProps => ({
  bookingInfo: getBookingInfo(state),
  loginError: errorSelectors.responseBody(state.errors, { queryKey: 'patientLogin', url: '' }),
  patientInfo: getBookingPatientInfo(state),
  session: state.session,
});

const mapDispatchToProps = (dispatch: Dispatch, { navigate }: OwnProps): DispatchProps => ({
  clearPatientData: (): UpdateBookingPatientInfo =>
    dispatch(updateBookingPatientInfo({ firstName: '', lastName: '', dob: '', phone: '' })),

  createAccount: (patient: PatientChartInfo): void => {
    dispatch(setPatientInfo(patient));

    // In case we were logged in and created a new patioent, we want to logout
    dispatch(logout());
    navigate('../confirmation');
  },

  updateEmail: (email: string): UpdateBookingPatientInfo =>
    dispatch(updateBookingPatientInfo({ email })),
});

const Account: React.FC<Props> = ({
  bookingInfo,
  createAccount,
  patientInfo,
  practiceId,
  session,
}) => {
  const { scheduledDate, physicianId, appointmentTypeId } = bookingInfo;

  const handleBackClick = (): void => {
    window.history.back();
    logEvent('login discarded');
  };

  if (!(scheduledDate && physicianId && appointmentTypeId)) {
    return <Redirect noThrow to={`book/${practiceId}/`} />;
  }

  if (session.activeUserId) {
    return <Redirect noThrow to={`book/${practiceId}/confirmation`} />;
  }

  return (
    <Content>
      <Button2 kind="link" onClick={handleBackClick} label="Back" icon="back" />
      <AppointmentSummary practiceId={practiceId} />
      <LoginCard
        patientInfo={patientInfo}
        practiceId={practiceId}
        setPatientDetails={createAccount}
      />
    </Content>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Account);
